import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Theme,
  ThemeContext as IThemeContext,
  ThemeColors,
  ThemeProviderProps,
  BuiltInTheme,
} from './types';

const ThemeContext = createContext<IThemeContext | undefined>(undefined);

// Built-in themes supported by the design system
const BUILT_IN_THEMES: BuiltInTheme[] = [
  'light',
  'dark',
  'cupcake',
  'business',
  'grove-light',
  'grove-dark',
  'dawn',
  'dusk',
  'twilight-light',
  'twilight-dark',
  'squad-light',
  'squad-dark',
  'lounge-light',
  'lounge-dark',
  'meadow-light',
  'meadow-dark',
  'focus-light',
  'focus-dark',
  'peach-light',
  'peach-dark',
];

// Script to inject for preventing FOUC
// Can be customized with defaultTheme parameter
export const themeInitScript = (defaultTheme?: string) => `
  (function() {
    const theme = localStorage.getItem('theme');
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    const fallbackTheme = ${defaultTheme ? `'${defaultTheme}'` : 'systemTheme'};
    document.documentElement.setAttribute('data-theme', theme || fallbackTheme);
  })();
`;

/**
 * ThemeProvider with automatic localStorage persistence and system preference detection.
 *
 * @example
 * // Basic usage - themes are automatically persisted
 * <ThemeProvider defaultTheme="grove-light">
 *   <App />
 * </ThemeProvider>
 *
 * // With theme change callback
 * <ThemeProvider
 *   defaultTheme="grove-dark"
 *   onThemeChange={(theme) => analytics.track('theme_changed', { theme })}
 * >
 *   <App />
 * </ThemeProvider>
 *
 * // Access theme state in components
 * const { theme, setTheme, themes } = useTheme();
 *
 * @param props - ThemeProvider configuration
 * @param props.children - Child components to wrap
 * @param props.defaultTheme - Default theme when no stored preference exists
 * @param props.initialTheme - Override theme for testing/Storybook (skips persistence)
 * @param props.onThemeChange - Callback fired when theme changes
 * @param props.disableInitialTransition - Prevent transition animation on initial load (default: true)
 * @param props.customThemes - Array of custom theme names to support (e.g., ['my-custom-dark', 'my-custom-light'])
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
  initialTheme,
  onThemeChange,
  disableInitialTransition = true,
  customThemes = [],
}) => {
  // Development-time prop validation
  if (typeof process !== 'undefined' && process.env && process.env['NODE_ENV'] === 'development') {
    // Check for common mistakes
    const propsToValidate = {
      children,
      defaultTheme,
      initialTheme,
      onThemeChange,
      disableInitialTransition,
      customThemes,
    };
    const invalidProps = Object.keys(propsToValidate as Record<string, unknown>).filter(
      (key) =>
        ![
          'children',
          'defaultTheme',
          'initialTheme',
          'onThemeChange',
          'disableInitialTransition',
          'customThemes',
        ].includes(key)
    );

    if (invalidProps.length > 0) {
      console.warn(
        `ThemeProvider: Invalid props detected: ${invalidProps.join(', ')}.\n` +
          `Valid props are: children, defaultTheme, initialTheme, onThemeChange, disableInitialTransition, customThemes.\n` +
          `See docs: https://design-system.orchard9.ai/theme`
      );
    }

    // Validate theme - check if it's a built-in theme or a custom theme
    const allThemes = [...BUILT_IN_THEMES, ...customThemes];
    if (defaultTheme && !allThemes.includes(defaultTheme)) {
      console.warn(
        `ThemeProvider: Unknown theme "${defaultTheme}".\n` +
          `Built-in themes: ${BUILT_IN_THEMES.join(', ')}.\n` +
          (customThemes.length > 0
            ? `Custom themes: ${customThemes.join(', ')}.\n`
            : `No custom themes provided. Use customThemes prop to add your own themes.\n`) +
          `Make sure to import the theme CSS file if using a custom theme.`
      );
    }
  }

  const [theme, setThemeState] = useState<Theme>(initialTheme || defaultTheme);
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');
  const [colors, setColors] = useState<ThemeColors>({} as ThemeColors);
  const [isInitialized, setIsInitialized] = useState(false);

  // Merge built-in themes with custom themes
  const themes = useMemo<Theme[]>(() => [...BUILT_IN_THEMES, ...customThemes], [customThemes]);

  // Get computed theme colors
  const updateColors = useCallback(() => {
    const computed = getComputedStyle(document.documentElement);
    const newColors: Partial<ThemeColors> = {};

    // DaisyUI color variables
    const colorVars = [
      'primary',
      'primary-focus',
      'primary-content',
      'secondary',
      'secondary-focus',
      'secondary-content',
      'accent',
      'accent-focus',
      'accent-content',
      'neutral',
      'neutral-focus',
      'neutral-content',
      'base-100',
      'base-200',
      'base-300',
      'base-content',
      'info',
      'success',
      'warning',
      'error',
    ];

    colorVars.forEach((color) => {
      const hsl = computed.getPropertyValue(`--${color}`).trim();
      if (hsl) {
        newColors[color as keyof ThemeColors] = `hsl(${hsl})`;
      }
    });

    setColors(newColors as ThemeColors);
  }, []);

  // Initialize theme from localStorage or system preference (skip if initialTheme provided)
  useEffect(() => {
    // If initialTheme is provided (e.g., from Storybook), don't override it
    if (initialTheme) {
      return;
    }

    const stored = localStorage.getItem('theme') as Theme | null;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    if (stored && themes.includes(stored)) {
      setThemeState(stored);
    } else if (defaultTheme && themes.includes(defaultTheme)) {
      // Respect defaultTheme before falling back to system preference
      setThemeState(defaultTheme);
    } else if (mediaQuery.matches) {
      setThemeState('dark');
    } else {
      setThemeState('light');
    }

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
      if (!localStorage.getItem('theme')) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themes, initialTheme, defaultTheme]);

  // Apply theme and update colors
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);

    // Only apply transition after initial load to prevent FOUC
    if (disableInitialTransition && !isInitialized) {
      // Skip transition on initial load
    } else {
      document.documentElement.style.setProperty(
        'transition',
        'background-color 0.3s ease, color 0.3s ease'
      );
    }

    localStorage.setItem('theme', theme);

    // Update colors after theme change
    requestAnimationFrame(updateColors);

    // Emit theme change event
    window.dispatchEvent(new CustomEvent('themechange', { detail: { theme } }));

    // Call optional callback
    onThemeChange?.(theme);
  }, [theme, updateColors, onThemeChange, disableInitialTransition, isInitialized]);

  // Mark as initialized after first mount
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
  }, []);

  const value: IThemeContext = {
    theme,
    setTheme,
    themes,
    systemTheme,
    colors,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
